import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API } from '../api/client';
import type { ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { fecha } from '../lib/format';
import type { Permiso, Rol } from '../api/types';
import { PageHeader } from '../components/PageHeader';
import { MobileFormSheet } from '../components/MobileFormSheet';
import { ResponsiveTable, type Column } from '../components/ResponsiveTable';

interface UsuarioAdmin {
  id: string;
  email: string;
  nombre: string;
  activo: boolean;
  createdAt: string;
  roles: { id: string; nombre: string }[];
}

type Tab = 'usuarios' | 'roles';

export function AdminPage() {
  const { tiene } = useAuth();
  const [tab, setTab] = useState<Tab>('usuarios');

  if (!tiene('usuarios:admin')) {
    return (
      <div className="card p-6 text-sm text-slate-500">
        No tienes permiso para administrar usuarios.
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <PageHeader title="Administración" subtitle="Gestión de usuarios, roles y permisos." />

      <div className="inline-flex rounded-lg bg-slate-100 p-1 text-sm w-full sm:w-auto">
        <SegButton active={tab === 'usuarios'} onClick={() => setTab('usuarios')}>Usuarios</SegButton>
        <SegButton active={tab === 'roles'} onClick={() => setTab('roles')}>Roles y permisos</SegButton>
      </div>

      {tab === 'usuarios' && <UsuariosTab />}
      {tab === 'roles' && <RolesTab />}
    </div>
  );
}

function SegButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 sm:flex-initial px-4 py-2 rounded-md font-medium transition ${
        active ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-800'
      }`}
    >
      {children}
    </button>
  );
}

function UsuariosTab() {
  const qc = useQueryClient();
  const [form, setForm] = useState({ email: '', nombre: '', password: '', roleIds: [] as string[] });
  const [editUser, setEditUser] = useState<UsuarioAdmin | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  const usuariosQ = useQuery({
    queryKey: ['usuarios'],
    queryFn: () => API.get<UsuarioAdmin[]>('/usuarios'),
  });

  const rolesQ = useQuery({
    queryKey: ['roles'],
    queryFn: () => API.get<Rol[]>('/roles'),
  });

  const crear = useMutation({
    mutationFn: () => API.post('/usuarios', form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['usuarios'] });
      setForm({ email: '', nombre: '', password: '', roleIds: [] });
      setError(null);
      setSheetOpen(false);
    },
    onError: (err) => setError((err as unknown as ApiError).message ?? 'Error'),
  });

  const asignarRoles = useMutation({
    mutationFn: ({ id, roleIds }: { id: string; roleIds: string[] }) =>
      API.post(`/usuarios/${id}/roles`, { roleIds }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['usuarios'] });
      setEditUser(null);
    },
    onError: (err) => setError((err as unknown as ApiError).message ?? 'Error'),
  });

  const eliminar = useMutation({
    mutationFn: (id: string) => API.del(`/usuarios/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['usuarios'] }),
    onError: (err) => setError((err as unknown as ApiError).message ?? 'Error'),
  });

  const formNode = (
    <form
      className="space-y-3"
      onSubmit={(e) => { e.preventDefault(); crear.mutate(); }}
    >
      <div>
        <label className="label">Nombre</label>
        <input className="input" value={form.nombre} onChange={(e) => setForm((s) => ({ ...s, nombre: e.target.value }))} required />
      </div>
      <div>
        <label className="label">Email</label>
        <input type="email" className="input" value={form.email} onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))} required />
      </div>
      <div>
        <label className="label">Contraseña</label>
        <input type="password" minLength={8} className="input" value={form.password} onChange={(e) => setForm((s) => ({ ...s, password: e.target.value }))} required />
      </div>
      <div>
        <label className="label">Roles iniciales</label>
        <div className="space-y-1 max-h-40 overflow-y-auto border border-slate-200 rounded p-2">
          {rolesQ.data?.map((r) => (
            <label key={r.id} className="flex items-center gap-2 text-sm py-1">
              <input
                type="checkbox"
                checked={form.roleIds.includes(r.id)}
                onChange={(e) => setForm((s) => ({
                  ...s,
                  roleIds: e.target.checked ? [...s.roleIds, r.id] : s.roleIds.filter((x) => x !== r.id),
                }))}
              />
              {r.nombre}
            </label>
          ))}
        </div>
      </div>
      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">{error}</div>}
      <button type="submit" className="btn-primary w-full" disabled={crear.isPending}>
        {crear.isPending ? 'Creando…' : 'Crear usuario'}
      </button>
    </form>
  );

  const columns: Column<UsuarioAdmin>[] = [
    {
      key: 'usuario',
      header: 'Usuario',
      mobile: 'primary',
      render: (u) => (
        <div>
          <div className="text-sm font-semibold">{u.nombre}</div>
          <div className="text-xs text-slate-500">{u.email}</div>
        </div>
      ),
    },
    {
      key: 'estado',
      header: 'Estado',
      mobile: 'secondary',
      align: 'right',
      render: (u) => (
        <span className={`pill ${u.activo ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
          {u.activo ? 'Activo' : 'Inactivo'}
        </span>
      ),
    },
    {
      key: 'roles',
      header: 'Roles',
      render: (u) => {
        const enEdicion = editUser?.id === u.id;
        if (enEdicion && editUser) {
          return (
            <div className="flex flex-wrap gap-1">
              {rolesQ.data?.map((r) => {
                const checked = editUser.roles.some((x) => x.id === r.id);
                return (
                  <label key={r.id} className={`pill cursor-pointer ${checked ? 'bg-brand-500 text-slate-900' : 'bg-slate-100 text-slate-500'}`}>
                    <input
                      type="checkbox"
                      className="hidden"
                      checked={checked}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? [...editUser.roles, { id: r.id, nombre: r.nombre }]
                          : editUser.roles.filter((x) => x.id !== r.id);
                        setEditUser({ ...editUser, roles: next });
                      }}
                    />
                    {r.nombre}
                  </label>
                );
              })}
            </div>
          );
        }
        return (
          <div className="flex flex-wrap gap-1 justify-end md:justify-start">
            {u.roles.length === 0 && <span className="text-xs text-slate-400">—</span>}
            {u.roles.map((r) => (
              <span key={r.id} className="pill bg-slate-100 text-slate-700">{r.nombre}</span>
            ))}
          </div>
        );
      },
    },
    {
      key: 'creado',
      header: 'Creado',
      render: (u) => <span className="text-xs text-slate-500">{fecha(u.createdAt)}</span>,
    },
  ];

  return (
    <div className="space-y-4 lg:grid lg:grid-cols-3 lg:gap-6 lg:space-y-0">
      <div className="hidden lg:block card p-5 lg:col-span-1">
        <h2 className="font-semibold mb-3">Nuevo usuario</h2>
        {formNode}
      </div>

      <MobileFormSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        title="Nuevo usuario"
        inlineOnDesktop={false}
      >
        {formNode}
      </MobileFormSheet>

      <div className="card lg:col-span-2">
        <ResponsiveTable<UsuarioAdmin>
          columns={columns}
          rows={usuariosQ.data}
          rowKey={(u) => u.id}
          loading={usuariosQ.isLoading}
          empty="Sin usuarios."
          actions={(u) => {
            const enEdicion = editUser?.id === u.id;
            if (enEdicion && editUser) {
              return (
                <>
                  <button
                    type="button"
                    className="text-brand-600 text-xs font-medium hover:underline"
                    onClick={() => asignarRoles.mutate({ id: u.id, roleIds: editUser.roles.map((r) => r.id) })}
                  >
                    Guardar roles
                  </button>
                  <button type="button" className="text-slate-500 text-xs font-medium hover:underline" onClick={() => setEditUser(null)}>
                    Cancelar
                  </button>
                </>
              );
            }
            return (
              <>
                <button type="button" className="text-brand-600 text-xs font-medium hover:underline" onClick={() => setEditUser(u)}>
                  Editar roles
                </button>
                <button
                  type="button"
                  className="text-red-600 text-xs font-medium hover:underline"
                  onClick={() => { if (confirm(`¿Eliminar a ${u.nombre}?`)) eliminar.mutate(u.id); }}
                >
                  Eliminar
                </button>
              </>
            );
          }}
        />
      </div>

      <button
        type="button"
        onClick={() => setSheetOpen(true)}
        className="fab lg:hidden"
        aria-label="Nuevo usuario"
      >
        +
      </button>
    </div>
  );
}

function RolesTab() {
  const qc = useQueryClient();
  const [form, setForm] = useState({ nombre: '', descripcion: '', permisoIds: [] as string[] });
  const [editRol, setEditRol] = useState<Rol | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  const rolesQ = useQuery({
    queryKey: ['roles'],
    queryFn: () => API.get<Rol[]>('/roles'),
  });

  const permisosQ = useQuery({
    queryKey: ['permisos'],
    queryFn: () => API.get<Permiso[]>('/roles/permisos'),
  });

  const crear = useMutation({
    mutationFn: () => API.post('/roles', form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['roles'] });
      setForm({ nombre: '', descripcion: '', permisoIds: [] });
      setError(null);
      setSheetOpen(false);
    },
    onError: (err) => setError((err as unknown as ApiError).message ?? 'Error'),
  });

  const actualizar = useMutation({
    mutationFn: (r: Rol) => API.put(`/roles/${r.id}`, {
      nombre: r.nombre,
      descripcion: r.descripcion,
      permisoIds: r.permisos.map((p) => p.id),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['roles'] });
      setEditRol(null);
    },
    onError: (err) => setError((err as unknown as ApiError).message ?? 'Error'),
  });

  const eliminar = useMutation({
    mutationFn: (id: string) => API.del(`/roles/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['roles'] }),
    onError: (err) => setError((err as unknown as ApiError).message ?? 'Error'),
  });

  const formNode = (
    <form
      className="space-y-3"
      onSubmit={(e) => { e.preventDefault(); crear.mutate(); }}
    >
      <div>
        <label className="label">Nombre</label>
        <input className="input" value={form.nombre} onChange={(e) => setForm((s) => ({ ...s, nombre: e.target.value }))} required />
      </div>
      <div>
        <label className="label">Descripción</label>
        <input className="input" value={form.descripcion} onChange={(e) => setForm((s) => ({ ...s, descripcion: e.target.value }))} />
      </div>
      <div>
        <label className="label">Permisos</label>
        <div className="space-y-1 max-h-60 overflow-y-auto border border-slate-200 rounded p-2">
          {permisosQ.data?.map((p) => (
            <label key={p.id} className="flex items-center gap-2 text-sm py-1">
              <input
                type="checkbox"
                checked={form.permisoIds.includes(p.id)}
                onChange={(e) => setForm((s) => ({
                  ...s,
                  permisoIds: e.target.checked ? [...s.permisoIds, p.id] : s.permisoIds.filter((x) => x !== p.id),
                }))}
              />
              <span className="font-mono text-xs">{p.clave}</span>
            </label>
          ))}
        </div>
      </div>
      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">{error}</div>}
      <button type="submit" className="btn-primary w-full" disabled={crear.isPending}>
        {crear.isPending ? 'Creando…' : 'Crear rol'}
      </button>
    </form>
  );

  return (
    <div className="space-y-4 lg:grid lg:grid-cols-3 lg:gap-6 lg:space-y-0">
      <div className="hidden lg:block card p-5 lg:col-span-1">
        <h2 className="font-semibold mb-3">Nuevo rol</h2>
        {formNode}
      </div>

      <MobileFormSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        title="Nuevo rol"
        inlineOnDesktop={false}
      >
        {formNode}
      </MobileFormSheet>

      <div className="lg:col-span-2 space-y-3 sm:space-y-4">
        {rolesQ.isLoading && <div className="card p-6 text-sm text-slate-400">Cargando…</div>}
        {rolesQ.data?.map((r) => {
          const enEdicion = editRol?.id === r.id;
          const current = enEdicion ? editRol : r;
          const permisoIds = current.permisos.map((p) => p.id);
          return (
            <div key={r.id} className="card p-4 sm:p-5 space-y-3">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
                <div className="min-w-0">
                  <h3 className="font-semibold">{r.nombre}</h3>
                  {r.descripcion && <p className="text-xs text-slate-500">{r.descripcion}</p>}
                </div>
                <div className="flex gap-3 text-xs">
                  {enEdicion ? (
                    <>
                      <button type="button" className="text-brand-600 font-medium hover:underline" onClick={() => actualizar.mutate(editRol!)}>
                        Guardar
                      </button>
                      <button type="button" className="text-slate-500 font-medium hover:underline" onClick={() => setEditRol(null)}>
                        Cancelar
                      </button>
                    </>
                  ) : (
                    <>
                      <button type="button" className="text-brand-600 font-medium hover:underline" onClick={() => setEditRol(r)}>
                        Editar permisos
                      </button>
                      <button
                        type="button"
                        className="text-red-600 font-medium hover:underline"
                        onClick={() => { if (confirm(`¿Eliminar rol "${r.nombre}"?`)) eliminar.mutate(r.id); }}
                      >
                        Eliminar
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-1">
                {permisosQ.data?.map((p) => {
                  const checked = permisoIds.includes(p.id);
                  if (!enEdicion && !checked) return null;
                  return (
                    <label
                      key={p.id}
                      className={`pill font-mono text-[10px] ${
                        enEdicion ? 'cursor-pointer' : ''
                      } ${checked ? 'bg-brand-500 text-slate-900' : 'bg-slate-100 text-slate-400'}`}
                    >
                      {enEdicion && (
                        <input
                          type="checkbox"
                          className="hidden"
                          checked={checked}
                          onChange={(e) => {
                            if (!editRol) return;
                            const nextPerms = e.target.checked
                              ? [...editRol.permisos, { id: p.id, clave: p.clave, descripcion: p.descripcion }]
                              : editRol.permisos.filter((x) => x.id !== p.id);
                            setEditRol({ ...editRol, permisos: nextPerms });
                          }}
                        />
                      )}
                      {p.clave}
                    </label>
                  );
                })}
                {!enEdicion && permisoIds.length === 0 && (
                  <span className="text-xs text-slate-400">Sin permisos asignados</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={() => setSheetOpen(true)}
        className="fab lg:hidden"
        aria-label="Nuevo rol"
      >
        +
      </button>
    </div>
  );
}
